import React, { useState, useEffect } from "react";
import axios from "axios";
import { PlusIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useConfirm } from "../contexts/ConfirmContext";

const Presentation = () => {
  const { confirm } = useConfirm();
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPresentation, setSelectedPresentation] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "slide",
    file: null,
    thumbnail: null,
    isActive: true,
  });

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

  useEffect(() => {
    fetchPresentations();
  }, []);

  const fetchPresentations = async () => {
    try {
      const response = await axios.get(`${API_URL}/presentations`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setPresentations(response.data);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch presentations");
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? checked : type === "file" ? files[0] : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null) {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (selectedPresentation) {
        await axios.put(
          `${API_URL}/presentations/${selectedPresentation.id}`,
          formDataToSend,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } else {
        await axios.post(`${API_URL}/presentations`, formDataToSend, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        });
      }
      setShowAddModal(false);
      setSelectedPresentation(null);
      setFormData({
        title: "",
        description: "",
        type: "slide",
        file: null,
        thumbnail: null,
        isActive: true,
      });
      fetchPresentations();
    } catch (err) {
      setError("Failed to save presentation");
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "Delete presentation",
      message: "Are you sure you want to delete this presentation?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API_URL}/presentations/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      fetchPresentations();
    } catch (err) {
      setError("Failed to delete presentation");
    }
  };

  const handleEdit = (presentation) => {
    setSelectedPresentation(presentation);
    setFormData({
      title: presentation.title,
      description: presentation.description,
      type: presentation.type,
      file: null,
      thumbnail: null,
      isActive: presentation.isActive,
    });
    setShowAddModal(true);
  };

  const filteredPresentations = presentations.filter(
    (presentation) =>
      presentation.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      presentation.description
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row flex-grow h-full bg-white rounded-lg shadow overflow-hidden">
      {/* Left Column - Presentation List */}
      <div className="w-full lg:w-1/3 border-b lg:border-b-0 lg:border-r border-gray-200 overflow-y-auto">
        <div className="px-4 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <div className="flex items-center space-x-4 w-full sm:w-auto">
              {/* Search Input */}
              <div className="relative w-48">
                <input
                  type="text"
                  placeholder="Search presentations"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
                <svg
                  className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 transform -translate-y-1/2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              {/* Add Presentation Button */}
              <button
                onClick={() => {
                  setSelectedPresentation(null);
                  setFormData({
                    title: "",
                    description: "",
                    type: "slide",
                    file: null,
                    thumbnail: null,
                    isActive: true,
                  });
                  setShowAddModal(true);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <PlusIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {filteredPresentations.map((presentation) => (
              <div
                key={presentation.id}
                className={`flex items-center p-3 sm:p-4 rounded-lg cursor-pointer ${
                  selectedPresentation?.id === presentation.id
                    ? "bg-indigo-50 border border-indigo-200"
                    : "hover:bg-gray-50"
                }`}
                onClick={() => setSelectedPresentation(presentation)}
              >
                <div className="flex-shrink-0">
                  {presentation.thumbnail ? (
                    <img
                      src={`${API_URL}${presentation.thumbnail}`}
                      alt={presentation.title}
                      className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                      <span className="text-gray-400 text-xs sm:text-sm">
                        No image
                      </span>
                    </div>
                  )}
                </div>
                <div className="ml-3 sm:ml-4 flex-grow min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 truncate">
                    {presentation.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500">
                    {presentation.type}
                  </p>
                  <div className="mt-1 flex items-center">
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full ${
                        presentation.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {presentation.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(presentation);
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(presentation.id);
                    }}
                    className="text-red-500 hover:text-red-700"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Column - Presentation Details */}
      <div className="w-full lg:w-2/3 overflow-y-auto p-4 sm:p-6">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {selectedPresentation ? (
          <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 break-words">
                {selectedPresentation.title}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Presentation Information
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <dl className="space-y-4">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Type
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {selectedPresentation.type}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Status
                        </dt>
                        <dd className="mt-1">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              selectedPresentation.isActive
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {selectedPresentation.isActive
                              ? "Active"
                              : "Inactive"}
                          </span>
                        </dd>
                      </div>
                      {selectedPresentation.file && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            File
                          </dt>
                          <dd className="mt-1">
                            <a
                              href={`${API_URL}${selectedPresentation.file}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              View Presentation
                            </a>
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Description
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">
                      {selectedPresentation.description}
                    </p>
                  </div>
                </div>
              </div>

              {selectedPresentation.thumbnail && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Thumbnail
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <img
                      src={`${API_URL}${selectedPresentation.thumbnail}`}
                      alt={selectedPresentation.title}
                      className="w-full h-auto rounded-lg"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500">
              Select a presentation to view details
            </p>
          </div>
        )}
      </div>

      {/* Add/Edit Presentation Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium mb-4">
              {selectedPresentation
                ? "Edit Presentation"
                : "Add New Presentation"}
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    required
                    value={formData.title}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Type
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    <option value="slide">Slide</option>
                    <option value="document">Document</option>
                    <option value="video">Video</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Presentation File
                  </label>
                  <input
                    type="file"
                    name="file"
                    onChange={handleInputChange}
                    className="mt-1 block w-full"
                    accept=".pdf,.ppt,.pptx,.doc,.docx,.mp4"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Thumbnail
                  </label>
                  <input
                    type="file"
                    name="thumbnail"
                    onChange={handleInputChange}
                    className="mt-1 block w-full"
                    accept="image/*"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setSelectedPresentation(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
                >
                  {selectedPresentation ? "Update" : "Add"} Presentation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Presentation;
